import React, {
    Fragment,
    useEffect,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
    Card,
    CardBody,
} from 'reactstrap'
import Select from 'react-select'
import FalconCardHeader from '../../../components/common/FalconCardHeader'
import ButtonIcon from '../../../components/common/ButtonIcon'
import DevicesTable from './DevicesTable'
import type { DeviceRow } from './DevicesTable'
import TableTabs from './TableTabs'
import withRedirect from '../../../components/auth/withRedirect'
import type { WithRedirectProps } from '../../../components/auth/withRedirect'
import { stringContainsSubstring } from '../../../components/shared/search-substring'
import { EditDevice } from '../forms/edit-device'
import { useAddUrlQuery } from '../../../components/shared/add-url-query'
import useSearchState from '../../../components/shared/use-search-state'
import { readLocationsIP } from '../helpers'
import type { RootState } from '../../../store'

/** Location IP option shape as stored in Redux */
interface LocationIPOption {
    uid: string
    name: string
}

/** react-select option shape */
interface SelectOption {
    value: string
    label: string
}

/** Auth user shape (minimal — full shape lives in auth reducer) */
interface AuthUser {
    superAdmin?: boolean
    [key: string]: unknown
}

/** Available preset sort values for the filter-panel selector */
type SortPreset = 'name' | 'status'

const SORT_PRESETS: SortPreset[] = ['name', 'status']

/** Default sort value used when no `?sort` query is present */
const DEFAULT_SORT: SortPreset = 'name'

/** Default rows-per-page used when no `?perPage` query is present */
const DEFAULT_PER_PAGE = 20

interface TableCardOwnProps {
    /** Callback to reload the device list after a mutating action */
    reloadData: () => void
    /** Current monitoring filter; `true` = monitored, `false` = not monitored, `null` = all */
    filterMonitored: boolean | null
}

type TableCardProps = TableCardOwnProps & WithRedirectProps

/**
 * Card containing the device search/filter controls and the data table.
 *
 * Wrapped with `withRedirect` so `setRedirect` is injected by the HOC.
 */
const TableCard = ({
    reloadData,
    filterMonitored,
    setRedirect,
}: TableCardProps) => {
    const [showFilter, setShowFilter] = useState(false)
    const [filterName, setFilterName] = useSearchState('', 'search', false)
    const [filterLocationIP, setFilterLocationIP] = useSearchState('', 'location', false)
    const [filteredDevices, setFilteredDevices] = useState<DeviceRow[]>([])
    const [pageRaw, setPageRaw] = useSearchState<string>('1', 'page', false)
    const [perPageRaw, setPerPageRaw] = useSearchState<string>(String(DEFAULT_PER_PAGE), 'perPage', false)
    const [sort, setSort] = useSearchState<string>(DEFAULT_SORT, 'sort', false)

    const page = Math.max(1, parseInt(String(pageRaw), 10) || 1)
    const perPage = Math.max(1, parseInt(String(perPageRaw), 10) || DEFAULT_PER_PAGE)

    const authedUser = useSelector((state: RootState) => state.auth.user) as AuthUser | null
    const { t } = useTranslation()

    const devices = useSelector((state: RootState) => state.devices.data) as DeviceRow[]
    const locationsIP = useSelector(
        (state: RootState) => (state.superAdmin as { locationsIP?: LocationIPOption[] })?.locationsIP ?? [],
    )
    const addUrlQuery = useAddUrlQuery()

    useEffect(() => {
        readLocationsIP()
    }, [])

    useEffect(() => {
        const trimmedName = (filterName || '').trim()
        const hasNameFilter = trimmedName !== ''
        const hasLocationFilter = !!filterLocationIP

        if (!hasNameFilter && !hasLocationFilter) {
            setFilteredDevices(devices)
            return
        }

        const devicesToFilter = [...(devices || [])]
            .filter(device => !hasNameFilter || stringContainsSubstring((device.name ?? '') + (device.ipAddress ?? ''), trimmedName))
            .filter(device => !hasLocationFilter || device.locationIP === filterLocationIP)

        setFilteredDevices(devicesToFilter)
    }, [filterName, filterLocationIP, devices])

    const locationOptions: SelectOption[] = locationsIP
        .map(location => ({
            value: location.uid,
            label: location.name,
        }))
        .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label))

    const selectedLocationOption: SelectOption | null =
        locationOptions.find(option => option.value === filterLocationIP) ?? null

    const sortOptions: SelectOption[] = [
        { value: 'name', label: t('device.sort.byName') },
        { value: 'status', label: t('device.sort.byStatus') },
    ]

    const selectedSortOption: SelectOption | null =
        SORT_PRESETS.includes(sort as SortPreset)
            ? (sortOptions.find(option => option.value === sort) ?? null)
            : null

    const handlePageChange = (nextPage: number): void => {
        const value = Math.max(1, nextPage | 0)
        setPageRaw(String(value))
        addUrlQuery('page', value === 1 ? null : value)
    }

    const handlePerPageChange = (nextPerPage: number, nextPage: number): void => {
        const perPageValue = Math.max(1, nextPerPage | 0)
        const pageValue = Math.max(1, nextPage | 0)
        setPerPageRaw(String(perPageValue))
        setPageRaw(String(pageValue))
        addUrlQuery('perPage', perPageValue === DEFAULT_PER_PAGE ? null : perPageValue)
        addUrlQuery('page', pageValue === 1 ? null : pageValue)
    }

    const handleSortChange = (nextSort: string): void => {
        setSort(nextSort)
        addUrlQuery('sort', nextSort === DEFAULT_SORT ? null : nextSort)
    }

    const handlePresetSortChange = (preset: SortPreset): void => {
        handleSortChange(preset)
        setPageRaw('1')
        addUrlQuery('page', null)
    }

    return (
        <Card className='mb-3'>
            <FalconCardHeader title={t('device.devices')} light={false}>
                <Fragment>
                    {authedUser?.superAdmin && (
                        <>
                            <EditDevice
                                updatedDevice={undefined}
                                onModalClose={reloadData}
                            >
                                <ButtonIcon
                                    icon='plus'
                                    transform='shrink-3 down-2'
                                    color='primary'
                                    size='sm'
                                >
                                    {t('device.addDevice')}
                                </ButtonIcon>
                            </EditDevice>
                            <ButtonIcon
                                icon='plus'
                                transform='shrink-3 down-2'
                                color='secondary'
                                size='sm'
                                onClick={() => setRedirect('/devices/locations-ip')}
                                className="ml-2"
                            >
                                {t('device.locationIPList')}
                            </ButtonIcon>
                        </>
                    )}
                    <ButtonIcon
                        icon='filter'
                        transform='shrink-3 down-2'
                        color='falcon-default'
                        size='sm'
                        className='mx-2'
                        onClick={() => setShowFilter(!showFilter)}
                    >
                        {t('filter')}
                    </ButtonIcon>

                    {(showFilter || filterName || filterLocationIP) && (
                        <div className="p-2 mt-2">
                            <input
                                value={filterName || ''}
                                type="search"
                                ref={(node) =>
                                    node?.style.setProperty('border-right', '1px solid #d8e2ef', 'important')
                                }
                                autoFocus
                                className="form-control form-control-lg"
                                placeholder="Zadejte Název, nebo IP"
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setFilterName(event.target.value)
                                    setPageRaw('1')
                                    addUrlQuery('page', null)
                                    addUrlQuery('search', event.target.value)
                                }}
                            />
                            <div className="mt-2">
                                <Select<SelectOption>
                                    value={selectedLocationOption}
                                    options={locationOptions}
                                    isClearable
                                    classNamePrefix="select"
                                    placeholder={t('device.locationIPFilterPlaceholder')}
                                    onChange={(selectedOption) => {
                                        const nextValue = selectedOption ? selectedOption.value : ''
                                        setFilterLocationIP(nextValue)
                                        setPageRaw('1')
                                        addUrlQuery('page', null)
                                        addUrlQuery('location', nextValue || null)
                                    }}
                                />
                            </div>
                            <div className="mt-2">
                                <Select<SelectOption>
                                    value={selectedSortOption}
                                    options={sortOptions}
                                    classNamePrefix="select"
                                    placeholder={t('device.sort.placeholder')}
                                    onChange={(selectedOption) => {
                                        if (!selectedOption) {
                                            return
                                        }
                                        handlePresetSortChange(selectedOption.value as SortPreset)
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </Fragment>
            </FalconCardHeader>
            <CardBody className="p-0">
                <TableTabs filterMonitored={filterMonitored} />
                <DevicesTable
                    devices={filteredDevices}
                    reloadData={reloadData}
                    page={page}
                    setPage={handlePageChange}
                    perPage={perPage}
                    setPerPage={handlePerPageChange}
                    sort={sort || DEFAULT_SORT}
                    setSort={handleSortChange}
                    backlink={btoa(JSON.stringify({ link: '/' + window.location.href.split(/\/cs\/|\/en\//)[1] }))}
                />
            </CardBody>
        </Card>
    )
}

export default withRedirect(TableCard)
